/**
 * Frontend date utility functions
 * Handles both date-only fields (YYYY-MM-DD) and timestamp fields properly
 */

/**
 * Check if a date string is date-only (YYYY-MM-DD) vs timestamp
 */
export function isDateOnly(dateString: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

/**
 * Format date for display, handling both date-only and timestamp fields
 * For date-only strings (YYYY-MM-DD), parse without timezone conversion
 * For timestamps, use normal Date parsing
 */
export function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
        // For date-only strings (YYYY-MM-DD), parse differently to avoid timezone issues
        if (isDateOnly(dateString)) {
            const [year, month, day] = dateString.split('-').map(Number);
            const date = new Date(year, month - 1, day); // Month is 0-indexed
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } else {
            // For timestamps, use normal parsing
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
    } catch (error) {
        return 'Invalid Date';
    }
}

/**
 * Format time for display, handling date-only fields
 * Returns empty string for date-only fields instead of showing time
 */
export function formatTime(dateString: string): string {
    if (!dateString) return '';
    if (isDateOnly(dateString)) return ''; // No time for date-only fields
    try {
        return new Date(dateString).toLocaleTimeString();
    } catch (error) {
        return '';
    }
}

/**
 * Format date and time for display (used for timestamps like transaction dates)
 * Example: "Jan 15, 2025, 3:45 PM"
 */
export function formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        return 'Invalid Date';
    }
}

/**
 * Format time string from database (handles time with timezone)
 * Example: "20:00:00+00" -> "8:00 PM"
 */
export function formatTimeString(timeString: string): string {
    if (!timeString) return '';
    
    try {
        // Handle time with timezone (e.g., "20:00:00+00", "17:00:00-04") or without timezone (e.g., "20:00:00")
        let timeOnly = timeString;
        
        // Remove timezone suffix if present
        // Handle both +XX and -XX timezone formats
        if (timeString.includes('+')) {
            timeOnly = timeString.split('+')[0];
        } else if (timeString.includes('-') && timeString.match(/.*-\d{2}$/)) {
            // Only split on dash if it's followed by timezone (2 digits at end)
            timeOnly = timeString.substring(0, timeString.lastIndexOf('-'));
        }
        
        const [hours, minutes] = timeOnly.split(':').map(Number);
        
        // Create a date object for formatting
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        
        return date.toLocaleTimeString([], { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        console.error('Error formatting time string:', timeString, error);
        return timeString; // Return original if formatting fails
    }
}

/**
 * Convert a Date object to YYYY-MM-DD string without timezone conversion
 * This is crucial for date-only fields to avoid timezone issues
 */
export function formatDateForInput(date: Date): string {
    if (!date || !(date instanceof Date)) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD string to Date object without timezone conversion
 * This ensures the Date object represents the exact date specified
 * Uses UTC to avoid any local timezone interpretation
 */
export function parseDateOnlyString(dateString: string): Date {
    if (!dateString || !isDateOnly(dateString)) {
        return new Date();
    }
    
    const [year, month, day] = dateString.split('-').map(Number);
    // Use UTC constructor and then convert to local midnight
    // This ensures we get the exact date regardless of timezone
    const utcDate = new Date(Date.UTC(year, month - 1, day));
    
    // Convert to local date at midnight
    const localDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    return localDate;
}

/**
 * Parse session date from backend to get the actual date without timezone conversion
 * Backend sends dates like "2025-09-28T04:00:00.000Z" but we want the date part (2025-09-28)
 * This extracts just the date part and parses it correctly
 */
export function parseSessionDate(dateString: string): Date {
    if (!dateString) return new Date();
    
    try {
        // Extract just the date part (YYYY-MM-DD) from timestamp
        const datePart = dateString.split('T')[0];
        
        if (isDateOnly(datePart)) {
            return parseDateOnlyString(datePart);
        }
        
        // Fallback to regular date parsing
        return new Date(dateString);
    } catch (error) {
        console.error('Error parsing session date:', dateString, error);
        return new Date();
    }
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
}
